import { ContextGuide } from '../models/context-guide.model';

export const CONTEXT_GUIDES_REGISTRY: Record<string, ContextGuide> = {
  '/admin/dashboard': {
    routePath: '/admin/dashboard',
    title: 'Dashboard de Administración',
    subtitle: 'Vista general del negocio',
    badge: 'Panel',
    description: 'Aquí ves de un vistazo el estado de tu negocio: ventas del día, caja, turnos y alertas relevantes.',
    steps: [
      {
        title: 'Revisá los indicadores clave',
        description: 'Las tarjetas superiores muestran ventas, ingresos y turnos del período seleccionado.',
        icon: 'insights'
      },
      {
        title: 'Filtrá por sucursal o período',
        description: 'Usá los filtros para acotar la información a una sucursal o rango de fechas específico.',
        icon: 'filter_alt'
      },
      {
        title: 'Navegá a los módulos',
        description: 'Desde los accesos rápidos podés ir directo a ventas, caja o turnos.',
        icon: 'open_in_new'
      }
    ],
    quickActions: [
      { label: 'Ir a Caja Diaria', route: '/finance/cash/daily' },
      { label: 'Ir a Mi Agenda', route: '/admin/my-agenda' }
    ]
  },
  '/finance/cash/daily': {
    routePath: '/finance/cash/daily',
    title: 'Caja Diaria',
    subtitle: 'Apertura, cobros y cierre operativo del día',
    badge: 'Caja',
    description: 'Control de la caja de la sucursal en curso: apertura del turno, ingresos y gastos manuales, seguimiento del efectivo por medio de pago y arqueo de cierre. Sin caja abierta no se puede cobrar ninguna venta.',
    steps: [
      {
        title: 'Abrí la caja',
        description: 'Si figura "Caja Cerrada", tocá "Iniciar Caja" e ingresá el efectivo inicial. Viene sugerido el fondo que dejaste al cerrar la caja anterior.',
        icon: 'lock_open'
      },
      {
        title: 'Leé las tarjetas del día',
        description: 'Monto inicial es el fondo de apertura. Ingreso neto suma los cobros y resta las devoluciones. Egresos son los gastos. Balance esperado (efectivo) es el efectivo físico que debería haber en el cajón.',
        icon: 'insights'
      },
      {
        title: 'Cargá ingresos y gastos manuales',
        description: 'Usá "Nuevo Ingreso (+)" o "Registrar Gasto (-)" para movimientos que no vienen de una venta, con categoría, medio de pago y monto. Las devoluciones no se cargan acá: se descuentan solas cuando las hacés desde la venta.',
        icon: 'sync_alt'
      },
      {
        title: 'Mirá los vales de saldo a favor',
        description: 'Si hubo vales, aparece un bloque aparte con lo pagado con vales, los vales emitidos y los usados en esta caja. Un vale no es dinero: no suma a ingresos, ni al efectivo, ni al arqueo.',
        icon: 'confirmation_number'
      },
      {
        title: 'Revisá el detalle por medio de pago',
        description: 'La tabla muestra, por cada medio, ingresos, gastos, devoluciones y neto en caja. Podés verla Detallada o Por Tipo, y como Tabla, Barras o Torta.',
        icon: 'pie_chart'
      },
      {
        title: 'Cerrá la caja (arqueo)',
        description: 'Tocá "Cierre de Caja", elegí quién cierra, contá el efectivo y repartilo entre "Fondo para apertura de mañana" y "Efectivo a retirar": entre los dos tienen que sumar el efectivo esperado. La diferencia se marca en rojo o verde. Bajá hasta el final del diálogo para habilitar "Cerrar Caja".',
        icon: 'lock'
      },
      {
        title: 'Finalizá el arqueo',
        description: 'Al cerrar se muestra el Resultado de Auditoría: estado del arqueo, contado contra esperado, reparto del efectivo, verificación por medio de pago y ventas por vendedor. Podés imprimir el ticket y tocar "Finalizar Arqueo".',
        icon: 'fact_check'
      }
    ],
    quickActions: [
      { label: 'Movimientos Financieros', route: '/finance/cash/movements' },
      { label: 'Historial de Cajas', route: '/finance/cash/history' }
    ]
  },
  '/finance/cash/history': {
    routePath: '/finance/cash/history',
    title: 'Historial de Cajas',
    subtitle: 'Auditoría de sesiones de caja por sucursal',
    badge: 'Caja',
    description: 'Listado completo de sesiones de caja (abiertas y cerradas) de la sucursal, con acceso al detalle de auditoría de cada cierre.',
    steps: [
      {
        title: 'Consultá el listado de sesiones',
        description: 'Filtrá por rango de fechas para ver las cajas abiertas y cerradas, con su responsable, monto inicial, monto final y diferencia de arqueo.',
        icon: 'filter_alt'
      },
      {
        title: 'Cerrá una caja abierta',
        description: 'Si encontrás una sesión sin cerrar (por ejemplo, de un turno anterior), usá el botón de candado en "Acciones" para completar su arqueo y cierre.',
        icon: 'lock'
      },
      {
        title: 'Auditá una caja cerrada',
        description: 'Hacé click en "Ver Auditoría" para abrir el detalle completo: fondo inicial, ingresos, egresos, monto final, notas de cierre y el listado de todas las operaciones registradas en esa sesión.',
        icon: 'analytics'
      },
      {
        title: 'Detectá diferencias de arqueo',
        description: 'La columna "Diferencia" resalta en rojo o verde si el efectivo contado no coincidió con el balance esperado al momento del cierre.',
        icon: 'warning_amber'
      }
    ],
    quickActions: [
      { label: 'Caja Diaria', route: '/finance/cash/daily' },
      { label: 'Movimientos Financieros', route: '/finance/cash/movements' }
    ]
  },
  '/finance/cash/movements': {
    routePath: '/finance/cash/movements',
    title: 'Movimientos Financieros',
    subtitle: 'Trazabilidad completa de ingresos y egresos',
    badge: 'Finanzas',
    description: 'Vista consolidada de todos los movimientos de dinero de la sucursal, sin importar su origen: cobros de ventas, gastos de caja, cobros de turnos de agenda y devoluciones.',
    steps: [
      {
        title: 'Filtrá por rango de fechas',
        description: 'Acotá el listado al período que querés analizar usando el selector de fechas.',
        icon: 'filter_alt'
      },
      {
        title: 'Revisá los indicadores del período',
        description: 'Las tarjetas superiores resumen ingresos, egresos, balance neto y cantidad total de movimientos del rango filtrado.',
        icon: 'insights'
      },
      {
        title: 'Identificá el origen de cada movimiento',
        description: 'La columna "Origen" indica si el movimiento proviene de Ventas, Gastos, Agenda o Caja, y marca con una etiqueta las devoluciones y anulaciones.',
        icon: 'shopping_bag'
      },
      {
        title: 'Navegá a la venta original',
        description: 'En las devoluciones o anulaciones, hacé click en la descripción del movimiento para ir directo al detalle de la venta que le dio origen.',
        icon: 'open_in_new'
      }
    ],
    quickActions: [
      { label: 'Caja Diaria', route: '/finance/cash/daily' },
      { label: 'Historial de Cajas', route: '/finance/cash/history' }
    ]
  },
  '/finance/expenses': {
    routePath: '/finance/expenses',
    title: 'Gastos Administrativos',
    subtitle: 'Control presupuestario y proyecciones de caja',
    badge: 'Finanzas',
    description: 'Gestión integral del flujo de egresos del negocio. Permite conciliar gastos reales ejecutados y visualizar proyecciones automáticas para prevenir sobrecostos.',
    steps: [
      {
        title: 'Diferencia entre Gastos y Proyecciones',
        description: 'Los registros con indicador de reloj son proyecciones automáticas basadas en tus plantillas activas. Los registros sólidos representan gastos reales devengados.',
        icon: 'schedule'
      },
      {
        title: 'Ejecución de Pago Anticipado',
        description: 'Para confirmar el pago de una proyección antes de la fecha programada, hacé click en el botón de acción de la fila. Esto transformará la proyección en un gasto real asignando el medio de pago.',
        icon: 'payments'
      },
      {
        title: 'Configuración de Plantillas Recurrentes',
        description: 'Automatizá alquileres, servicios, licencias o sueldos creando plantillas con reglas de vencimiento fijo para que el sistema proyecte tu flujo de caja a futuro.',
        icon: 'autorenew'
      },
      {
        title: 'Categorización y Filtros',
        description: 'Utilizá los filtros por rango de fechas o categorías para auditar desviaciones entre lo presupuestado y lo efectivamente pagado.',
        icon: 'filter_alt'
      }
    ],
    quickActions: [
      { label: 'Plantillas de Costos Fijos', route: '/finance/expenses/templates' },
      { label: 'Categorías de Gasto', route: '/admin/config/administrative-categories' }
    ]
  },
  '/admin/my-agenda': {
    routePath: '/admin/my-agenda',
    title: 'Agenda y Reservas',
    subtitle: 'Calendario operativo con cobro integrado',
    badge: 'Operaciones',
    description: 'Es el módulo más complejo del sistema: un calendario en tiempo real donde se crean, reprograman y cobran turnos, con reglas estrictas de caja para no perder plata ni pisar datos ya facturados.',
    steps: [
      {
        title: 'Elegí sucursal y profesional primero',
        description: 'Los filtros superiores son obligatorios: si intentás crear una cita sin sucursal y profesional seleccionados, el sistema te avisa y no deja continuar. El filtro de servicio es opcional, solo para acotar la vista.',
        icon: 'filter_alt'
      },
      {
        title: 'Armá el carrito de la cita',
        description: 'El total se arma solo: el servicio base, más las variantes que sumes, más los productos de stock que agregues. Cualquier cambio recalcula el total en vivo, antes de definir cómo se paga.',
        icon: 'shopping_cart'
      },
      {
        title: 'Sin Pago, Seña o Pago Total: la diferencia',
        description: '"Sin Pago" no cobra nada ahora y manda el 100% del saldo a la Cuenta Corriente del cliente (por eso exige tener un cliente cargado). "Seña" cobra una parte ahora con el medio que elijas, y el resto queda automáticamente en Cuenta Corriente. "Pago Total" busca saldar el importe completo en el momento.',
        icon: 'payments'
      },
      {
        title: 'Pagos parciales y Cuenta Corriente',
        description: 'Dentro de "Pago Total" podés elegir Total en Caja (todo con un medio físico), Total en C.C. (todo a cuenta corriente) o Parcial (definís cuánto entra en caja y el resto se manda solo a Cuenta Corriente). Mandar algo a Cuenta Corriente siempre requiere un cliente identificado; ese saldo queda como deuda cobrable después desde Cuentas Corrientes.',
        icon: 'call_split'
      },
      {
        title: 'Cómo impacta en la Caja',
        description: 'Todo monto asignado a un medio de pago físico (efectivo, tarjeta, transferencia, billetera) ingresa de inmediato a la caja diaria abierta de la sucursal. Lo que se manda a Cuenta Corriente no toca la caja en este momento: impacta recién cuando el cliente salda esa deuda desde el módulo de Cuentas Corrientes.',
        icon: 'point_of_sale'
      },
      {
        title: 'Pago Total no significa lo mismo siempre',
        description: 'Si elegís Pago Total al crear una cita nueva (antes del día de la atención), solo la confirma con el cobro anticipado: no puede quedar Completada todavía. Recién al editar la cita el día de la atención y volver a saldar el total, el sistema la marca como Completada.',
        icon: 'schedule'
      },
      {
        title: 'Los 4 estados de una cita',
        description: 'Pendiente: recién creada, sin nada pagado. Confirmada: tiene una seña o un pago anticipado. Completada: la atención ya se dio y el saldo quedó en cero. Cancelada: se liberó el horario.',
        icon: 'sync_alt'
      },
      {
        title: 'Reprogramá arrastrando la cita',
        description: 'Arrastrá una cita a otro horario o estirá su borde para cambiar la duración: se reprograma al instante sin necesidad de abrir el formulario. Pasá el mouse por encima de cualquier cita para ver un resumen rápido en un tooltip.',
        icon: 'drag_indicator'
      },
      {
        title: 'Cancelá sin perder lo ya cobrado',
        description: 'Cancelar una cita libera el horario en la agenda. Si tenía una seña o pago previo, ese dinero no se pierde: queda acreditado a favor del cliente en su Cuenta Corriente.',
        icon: 'event_busy'
      },
      {
        title: 'Citas ya cobradas quedan bloqueadas',
        description: 'Si una cita está Completada y ya tiene una venta asociada, al reabrirla el cliente y el servicio quedan bloqueados para editar: es un candado para que nadie descuadre una caja ya cerrada.',
        icon: 'lock'
      }
    ],
    quickActions: [
      { label: 'Gestionar Staff y Horarios', route: '/staff' },
      { label: 'Ver Clientes (CRM)', route: '/admin/clients' }
    ]
  },
  '/admin/sales': {
    routePath: '/admin/sales',
    title: 'Ventas Realizadas',
    subtitle: 'Historial, facturación y devoluciones',
    badge: 'Ventas',
    description: 'Listado de las ventas de la sucursal. Cada venta, cada devolución y cada anulación aparecen como un registro propio, con su medio de pago y su estado de facturación.',
    steps: [
      {
        title: 'Revisá los indicadores',
        description: 'Las tarjetas superiores muestran lo vendido, la cantidad de operaciones y las cancelaciones del período. Sin fechas elegidas se muestra el día de hoy. Punto de Venta puede consultar hasta 5 días hacia atrás.',
        icon: 'insights'
      },
      {
        title: 'Buscá una venta',
        description: 'Usá el buscador por producto, cliente o número de comprobante, y el selector de fechas para cambiar el período.',
        icon: 'search'
      },
      {
        title: 'Entendé las etiquetas',
        description: 'En "Pago" se ven los medios usados, incluido el vale (VALE V-...). En "Factura" figura Factura total, No facturado o el comprobante (por ejemplo, NC B 0001-...). Una venta anulada se ve atenuada, y su anulación aparece como una línea aparte.',
        icon: 'label'
      },
      {
        title: 'Facturá una venta',
        description: 'Si la venta figura como "No facturado", el ícono de comprobante de la fila abre el asistente de facturación con los datos ya cargados.',
        icon: 'receipt_long'
      },
      {
        title: 'Devolvé o anulá una venta',
        description: 'El ícono de devolución (junto al ojo) abre la Nota de Crédito: anula la venta completa, devuelve el stock y reintegra lo cobrado en dinero o en un vale. Ver el detalle en Facturación.',
        icon: 'undo'
      },
      {
        title: 'Abrí el detalle',
        description: 'El ícono de ojo muestra la operación completa: estado del pago, cliente, medios, productos, liquidación, vales emitidos (con botón Imprimir) y registro de auditoría.',
        icon: 'visibility'
      },
      {
        title: 'Mirá cómo se actualiza solo',
        description: 'Cuando ARCA aprueba una factura o una nota de crédito, la lista se refresca sola y aparece un aviso en pantalla. No hace falta recargar.',
        icon: 'sync'
      }
    ],
    quickActions: [
      { label: 'Nueva Venta', route: '/admin/sales/new' },
      { label: 'Vales', route: '/admin/sales/vouchers' },
      { label: 'Historial de Cierres', route: '/finance/cash/history' }
    ]
  },
  '/admin/sales/new': {
    routePath: '/admin/sales/new',
    title: 'Nueva Venta',
    subtitle: 'Punto de venta: carrito y cobro en dos pasos',
    badge: 'Ventas',
    description: 'Flujo de mostrador en dos pasos: armar el carrito y cobrar. Requiere una caja abierta en la sucursal; si no la hay, el sistema te lleva a abrirla antes de seguir.',
    steps: [
      {
        title: 'Paso 1: buscá y agregá productos',
        description: 'Escribí el nombre completo o el SKU. Cada resultado muestra su precio y cuánto stock hay. Tocalo para agregarlo y ajustá la cantidad con + y -.',
        icon: 'shopping_cart'
      },
      {
        title: 'Paso 1: elegí el cliente',
        description: '"Consumidor Final" es la venta anónima de mostrador. "Cliente CRM" permite elegir un cliente registrado.',
        icon: 'person'
      },
      {
        title: 'Paso 1: elegí el vendedor',
        description: 'En los locales que lo exigen, el campo "Vendedor / Operador" es obligatorio. Después tocá "Proceder al pago".',
        icon: 'badge'
      },
      {
        title: 'Paso 2: cargá un vale (opcional)',
        description: 'Si el cliente tiene un vale de saldo a favor, escribí o escaneá su código y tocá "Cargar". Se descuenta del total y queda por cobrar solo la diferencia. Con vales no rige el descuento del medio de pago. Un vale se quita con la X.',
        icon: 'confirmation_number'
      },
      {
        title: 'Paso 2: elegí la modalidad y el medio',
        description: '"Total Caja" cobra todo con un medio; "Combinada" reparte el cobro entre varios. Los medios son Efectivo, Tarjeta, Transferencia, QR y Mercado Pago. Cada uno puede tener planes (por ejemplo, efectivo con descuento o cuotas con recargo).',
        icon: 'payments'
      },
      {
        title: 'Paso 2: activá la factura si hace falta',
        description: '"Factura Electrónica (ARCA)" emite el comprobante apenas se cobra. Con la factura activada, al confirmar te lleva directo al asistente de facturación con el monto ya cargado.',
        icon: 'receipt_long'
      },
      {
        title: 'Confirmá la operación',
        description: 'Revisá el total a cobrar y tocá "Confirmar operación". La venta impacta en el stock y en la caja, y aparece en Ventas Realizadas.',
        icon: 'check_circle'
      }
    ],
    quickActions: [
      { label: 'Ventas Realizadas', route: '/admin/sales' },
      { label: 'Caja Diaria', route: '/finance/cash/daily' }
    ]
  },
  '/admin/inventory': {
    routePath: '/admin/inventory',
    title: 'Inventario de Productos',
    subtitle: 'Catálogo, stock y precios',
    badge: 'Inventario',
    description: 'Catálogo de productos con su costo, precio de venta, IVA y stock total y por sucursal. Desde acá se dan de alta, se editan y se actualizan en lote.',
    steps: [
      {
        title: 'Buscá y filtrá',
        description: 'Buscá por nombre o categoría, o por SKU. También podés filtrar por categoría y por estado (Activos). "Limpiar" quita los filtros.',
        icon: 'filter_alt'
      },
      {
        title: 'Leé la tabla',
        description: 'Cada fila muestra precio de costo, precio de venta, IVA, stock total y el stock por sucursal. Un stock en rojo con triángulo indica que no hay unidades.',
        icon: 'table_chart'
      },
      {
        title: 'Creá o editá un producto',
        description: '"Nuevo Producto" da de alta un ítem. El lápiz de cada fila edita precio, costo, categoría y demás datos.',
        icon: 'edit'
      },
      {
        title: 'Ajustá el stock',
        description: 'El ícono de caja de la fila abre un diálogo para sumar o restar unidades en la sucursal, sin pasar por una venta.',
        icon: 'inventory_2'
      },
      {
        title: 'Actualizá precios en lote',
        description: '"Actualizar Precios" hace un ajuste masivo de precios.',
        icon: 'price_change'
      },
      {
        title: 'Importá o exportá',
        description: '"Exportar" descarga el catálogo y "Importar Excel" lo lee desde una planilla con SKU, precio, costo, categoría y stock.',
        icon: 'upload_file'
      }
    ],
    quickActions: [
      { label: 'Categorías de Productos', route: '/admin/inventory/categories' },
      { label: 'Distribución de Inventario', route: '/admin/inventory/distribution' }
    ]
  },
  '/admin/inventory/distribution': {
    routePath: '/admin/inventory/distribution',
    title: 'Distribución de Inventario',
    subtitle: 'Transferencias de stock entre sucursales',
    badge: 'Inventario',
    description: 'Mueve stock desde una sucursal de origen (por ejemplo, un depósito central) hacia una de destino. El stock queda en tránsito hasta que el destino confirma la recepción.',
    steps: [
      {
        title: 'Elegí origen y destino',
        description: 'Seleccioná la sucursal de origen para ver sus productos disponibles y después el destino. No pueden ser la misma.',
        icon: 'swap_horiz'
      },
      {
        title: 'Cargá las cantidades',
        description: 'Definí cuánto transferir de cada producto. El sistema no deja superar el stock disponible en el origen.',
        icon: 'edit_note'
      },
      {
        title: 'Usá la sugerencia automática',
        description: 'Completa las cantidades según el déficit de stock mínimo de la sucursal de destino.',
        icon: 'auto_awesome'
      },
      {
        title: 'Despachá',
        description: 'El botón "Despachar" muestra cuántos ítems llevás. Al confirmar, el stock sale del origen y queda en tránsito.',
        icon: 'local_shipping'
      },
      {
        title: 'Seguí las distribuciones',
        description: '"Ver Distribuciones" lista los envíos y su estado. El stock se suma al destino recién cuando este confirma la recepción.',
        icon: 'track_changes'
      }
    ],
    quickActions: [
      { label: 'Inventario de Productos', route: '/admin/inventory' },
      { label: 'Categorías de Productos', route: '/admin/inventory/categories' }
    ]
  },
  '/admin/inventory/returns': {
    routePath: '/admin/inventory/returns',
    title: 'Devolución de Venta',
    subtitle: 'Reintegro parcial o total de una venta',
    badge: 'Inventario',
    description: 'Procesa la devolución de uno o más ítems de una venta ya confirmada, calculando el monto a reintegrar y, opcionalmente, restituyendo el stock al inventario.',
    steps: [
      {
        title: 'Elegí las cantidades a devolver',
        description: 'Por cada ítem de la venta original indicá cuántas unidades devolver. El sistema respeta lo que ya se haya devuelto en operaciones anteriores.',
        icon: 'undo'
      },
      {
        title: 'Decidí si el stock vuelve al inventario',
        description: 'El interruptor "Restaurar Stock" solo está disponible para productos físicos; los servicios no suman stock.',
        icon: 'inventory_2'
      },
      {
        title: 'Seleccioná el medio de reintegro',
        description: 'Efectivo genera un egreso en la caja diaria de hoy, Tarjeta/Digital registra una reversión electrónica, y Saldo a Favor acredita el importe en la cuenta corriente del cliente sin afectar la caja física.',
        icon: 'payments'
      },
      {
        title: 'Confirmá la devolución',
        description: 'Revisá el resumen del total a reintegrar antes de procesar: es una operación crítica que no se puede deshacer.',
        icon: 'check_circle'
      }
    ],
    quickActions: [
      { label: 'Ver Ventas', route: '/admin/sales' }
    ]
  },
  '/billing': {
    routePath: '/billing',
    title: 'Facturación Electrónica',
    subtitle: 'Comprobantes emitidos y su estado ante ARCA',
    badge: 'Facturación',
    description: 'Historial de facturas y notas de crédito del negocio, con su estado ante ARCA.',
    steps: [
      {
        title: 'Seguí el estado',
        description: 'Pendiente: enviado a ARCA, esperando respuesta. Aprobado: ya tiene número y CAE. Rechazado o Error: requiere tu atención. El estado se actualiza solo y aparece un aviso con el número y el CAE cuando ARCA aprueba.',
        icon: 'schedule'
      },
      {
        title: 'Identificá cada fila',
        description: 'Las notas de crédito aparecen en rojo, con importe negativo y la referencia a la factura que anulan (por ejemplo, Ref: Factura B N° 00.000.086).',
        icon: 'receipt_long'
      },
      {
        title: 'Buscá y filtrá',
        description: 'Buscá por cliente o comprobante, y filtrá por estado (Todos, Aprobados, Pendientes, Rechazados, Error Fatal).',
        icon: 'filter_alt'
      },
      {
        title: 'Imprimí el comprobante',
        description: 'Con el comprobante aprobado, el ícono de la impresora abre el comprobante en el formato de la impresora de la sucursal (A4 o ticket térmico), listo para imprimir o enviar al cliente.',
        icon: 'print'
      },
      {
        title: 'Emití una nota de crédito',
        description: 'En una factura aprobada y sin nota de crédito asociada, el ícono de nota de crédito abre la anulación de la venta.',
        icon: 'undo'
      },
      {
        title: 'Reintentá un error',
        description: 'Si un comprobante quedó rechazado o con error, "Reintentar" te lleva a corregir los datos y reenviarlo sin perder lo ya cargado.',
        icon: 'refresh'
      }
    ],
    quickActions: [
      { label: 'Nueva Factura', route: '/billing/new' },
      { label: 'Facturación Programada', route: '/billing/recurring' }
    ]
  },
  '/billing/new': {
    routePath: '/billing/new',
    title: 'Nueva Factura',
    subtitle: 'Emisión de comprobantes en dos pasos',
    badge: 'Facturación',
    description: 'Asistente para emitir un comprobante electrónico, normalmente desde una venta (con los datos ya cargados) o de forma manual.',
    steps: [
      {
        title: 'Paso 1: datos del cliente',
        description: 'Tipo y número de documento, razón social y condición frente al IVA. Para Consumidor Final se completan solos.',
        icon: 'person'
      },
      {
        title: 'Paso 1: clasificación',
        description: 'Tipo de comprobante (por ejemplo, Factura B según la condición del emisor), concepto (Productos, Servicios o Mixto) y punto de venta. Los conceptos con servicios piden además el período.',
        icon: 'description'
      },
      {
        title: 'Paso 1: detalle de la operación',
        description: 'Si tu organización factura por el total, se factura todo lo pendiente de la venta y no se pueden quitar ítems ni cambiar cantidades. Si no, elegís qué ítems y cuántas unidades facturar.',
        icon: 'list_alt'
      },
      {
        title: 'Paso 1: desglose de importes',
        description: 'Neto, exento, IVA y subtotal se calculan solos según la alícuota de cada ítem. Revisá y tocá "Siguiente".',
        icon: 'calculate'
      },
      {
        title: 'Paso 2: revisá y facturá',
        description: 'Controlá tipo, concepto, receptor, fechas de facturación y el resumen (neto, IVA y total). Después tocá "Emitir Comprobante".',
        icon: 'fact_check'
      },
      {
        title: 'Esperá la aprobación',
        description: 'El comprobante se envía a ARCA en forma asíncrona. Te lleva al historial, donde queda Pendiente y pasa solo a Aprobado, con un aviso que muestra el número y el CAE.',
        icon: 'send'
      }
    ],
    quickActions: [
      { label: 'Ver Comprobantes', route: '/billing' },
      { label: 'Facturación Programada', route: '/billing/recurring' }
    ]
  },
  '/billing/recurring': {
    routePath: '/billing/recurring',
    title: 'Facturación Programada',
    subtitle: 'Automatización de comprobantes recurrentes',
    badge: 'Facturación',
    description: 'Plantillas para generar y emitir automáticamente comprobantes fiscales de forma periódica (alquileres, abonos, servicios), sin tener que cargarlos a mano cada vez.',
    steps: [
      {
        title: 'Creá una plantilla recurrente',
        description: 'Definí el tipo de comprobante, el receptor, los montos y la frecuencia de emisión: diaria, semanal o mensual.',
        icon: 'autorenew'
      },
      {
        title: 'Consultá la próxima emisión',
        description: 'La tabla muestra cuándo se va a generar automáticamente el próximo comprobante de cada plantilla activa.',
        icon: 'schedule'
      },
      {
        title: 'Pausá o reanudá la automatización',
        description: 'Usá el interruptor de estado para activar o desactivar temporalmente una plantilla sin necesidad de borrarla.',
        icon: 'toggle_on'
      },
      {
        title: 'Editá una plantilla existente',
        description: 'Ajustá montos, frecuencia o datos del receptor desde el ícono de edición de cada fila.',
        icon: 'edit'
      }
    ],
    quickActions: [
      { label: 'Ver Comprobantes', route: '/billing' },
      { label: 'Nueva Factura', route: '/billing/new' }
    ]
  },
  '/billing/detail': {
    routePath: '/billing/detail',
    title: 'Detalle de Factura',
    subtitle: 'Comprobante, CAE y venta de origen',
    badge: 'Facturación',
    description: 'Vista de solo lectura de un comprobante ya emitido: sus datos fiscales completos, el comprobante relacionado si es una Nota de Crédito o Débito, y la venta que le dio origen.',
    steps: [
      {
        title: 'Revisá los datos fiscales',
        description: 'Tipo de comprobante, receptor, montos netos, IVA, total y el CAE otorgado por AFIP una vez aprobado.',
        icon: 'description'
      },
      {
        title: 'Imprimí el comprobante',
        description: 'Con el comprobante aprobado, el botón Imprimir abre el comprobante en el formato de la impresora de la sucursal (A4 o ticket térmico), listo para imprimir o compartir con el cliente.',
        icon: 'print'
      },
      {
        title: 'Consultá el comprobante relacionado',
        description: 'Si estás viendo una Nota de Crédito o Débito, la pantalla muestra automáticamente la factura original que anula o ajusta.',
        icon: 'link'
      },
      {
        title: 'Mirá la venta de origen',
        description: 'Si el comprobante nació de una venta, podés acceder al detalle completo de esa operación: ítems, medio de pago y cliente.',
        icon: 'shopping_bag'
      }
    ],
    quickActions: [
      { label: 'Ver Comprobantes', route: '/billing' },
      { label: 'Nueva Factura', route: '/billing/new' }
    ]
  },
  '/billing/edit': {
    routePath: '/billing/edit',
    title: 'Reintentar Factura',
    subtitle: 'Corregir y reenviar un comprobante rechazado',
    badge: 'Facturación',
    description: 'Formulario para corregir los datos de un comprobante que AFIP rechazó o que quedó en error fatal, y reenviarlo sin perder la vinculación con la venta original.',
    steps: [
      {
        title: 'Leé el motivo del rechazo',
        description: 'El mensaje devuelto por AFIP se muestra arriba del formulario para que sepas exactamente qué hay que corregir.',
        icon: 'error_outline'
      },
      {
        title: 'Revisá los datos precargados',
        description: 'El formulario llega con todos los valores del intento anterior (receptor, tipo de comprobante, montos, fechas); solo tenés que ajustar lo necesario.',
        icon: 'edit'
      },
      {
        title: 'Corregí lo que causó el error',
        description: 'Los motivos más comunes son datos del receptor mal cargados (CUIT/DNI inválido) o montos que no cierran con la alícuota de IVA seleccionada.',
        icon: 'build'
      },
      {
        title: 'Reenviá a AFIP',
        description: 'Al confirmar se genera un nuevo intento de emisión sobre el mismo comprobante. Vas a recibir una notificación en tiempo real cuando AFIP lo resuelva.',
        icon: 'send'
      }
    ],
    quickActions: [
      { label: 'Ver Comprobantes', route: '/billing' }
    ]
  },
  '/billing/credit-note-new': {
    routePath: '/billing/credit-note-new',
    title: 'Nota de Crédito',
    subtitle: 'Anulación de una venta facturada',
    badge: 'Facturación',
    description: 'Emite una nota de crédito que anula la factura original. Cuando ARCA la aprueba, los productos vuelven al stock y se reintegra lo cobrado.',
    steps: [
      {
        title: 'Verificá el comprobante original',
        description: 'Arriba se ven el cliente, el documento, el número de la factura y su total. Solo se pueden anular facturas aprobadas.',
        icon: 'fact_check'
      },
      {
        title: 'Elegí cómo se devuelve',
        description: '"Devolver dinero" arranca con el medio con el que se cobró; podés cambiarlo o dividir entre varios medios. "Vale" entrega un vale de saldo a favor por el importe: es al portador, de un solo uso y no saca dinero de la caja.',
        icon: 'payments'
      },
      {
        title: 'Controlá el dinero a devolver',
        description: 'A la derecha se muestra lo cobrado que todavía no se devolvió. Si devolvés en efectivo, no puede superar el efectivo que hay en la caja abierta.',
        icon: 'calculate'
      },
      {
        title: 'Revisá el comprobante de ajuste',
        description: 'El tipo de nota, el concepto, los artículos, el neto, el IVA y el total vienen de la factura original y no se editan. Podés elegir la fecha de emisión.',
        icon: 'receipt_long'
      },
      {
        title: 'Emití y confirmá',
        description: 'Tocá "Emitir Nota de Crédito" y confirmá con "Sí, emitir y anular". La nota queda Pendiente y se aprueba sola en unos segundos, con un aviso en pantalla.',
        icon: 'send'
      },
      {
        title: 'Si devolviste en vale',
        description: 'Imprimí el vale desde el detalle de la venta o desde Ventas, Vales, y entregáselo al cliente.',
        icon: 'confirmation_number'
      }
    ],
    quickActions: [
      { label: 'Ver Comprobantes', route: '/billing' },
      { label: 'Ventas Realizadas', route: '/admin/sales' },
      { label: 'Vales', route: '/admin/sales/vouchers' }
    ]
  },
  '/billing/recurring/new': {
    routePath: '/billing/recurring/new',
    title: 'Nueva Plantilla Recurrente',
    subtitle: 'Automatizar una Factura C periódica',
    badge: 'Facturación',
    description: 'Formulario para crear una plantilla de facturación automática. Por ser una operación recurrente sin intervención manual, el sistema siempre emite Factura C (Monotributo), sin desglose de IVA.',
    steps: [
      {
        title: 'Elegí sucursal y cliente',
        description: 'La sucursal define el punto de venta a facturar. El cliente del CRM es opcional: si lo dejás vacío, se factura a Consumidor Final.',
        icon: 'person'
      },
      {
        title: 'Cargá el ítem y el monto',
        description: 'Describí el concepto a facturar y el monto total. Al ser Factura C, ese monto se toma directamente como total, sin discriminar IVA.',
        icon: 'edit_note'
      },
      {
        title: 'Definí la frecuencia',
        description: 'Elegí si la emisión es diaria, semanal (con el día de la semana) o mensual (con el día del mes), y la hora en que se va a ejecutar.',
        icon: 'autorenew'
      },
      {
        title: 'Configurá el período y vencimiento',
        description: 'La regla de período determina a qué mes o ciclo corresponde cada emisión, y los días de vencimiento fijan la fecha límite de pago sugerida en el comprobante.',
        icon: 'event'
      }
    ],
    quickActions: [
      { label: 'Facturación Programada', route: '/billing/recurring' },
      { label: 'Ver Comprobantes', route: '/billing' }
    ]
  },
  '/billing/recurring/edit': {
    routePath: '/billing/recurring/edit',
    title: 'Editar Plantilla Recurrente',
    subtitle: 'Modificar una automatización existente',
    badge: 'Facturación',
    description: 'Edita los datos de una plantilla de facturación automática ya creada. Los cambios aplican a partir de la próxima emisión calculada; las facturas que ya se generaron no se modifican retroactivamente.',
    steps: [
      {
        title: 'Revisá los datos precargados',
        description: 'El formulario llega con la configuración actual de la plantilla: sucursal, cliente, ítem, monto y frecuencia.',
        icon: 'description'
      },
      {
        title: 'Ajustá monto o frecuencia',
        description: 'Podés cambiar el monto, la frecuencia (diaria, semanal o mensual) y el día u hora de ejecución. El sistema recalcula automáticamente la próxima fecha de emisión.',
        icon: 'edit'
      },
      {
        title: 'Actualizá el período y vencimiento',
        description: 'Modificá la regla de período o los días de vencimiento si cambiaron las condiciones del acuerdo con el cliente.',
        icon: 'event'
      },
      {
        title: 'Guardá los cambios',
        description: 'Al confirmar, la plantilla se actualiza y vas a ver la nueva próxima emisión reflejada en el listado de Facturación Programada.',
        icon: 'check_circle'
      }
    ],
    quickActions: [
      { label: 'Facturación Programada', route: '/billing/recurring' },
      { label: 'Ver Comprobantes', route: '/billing' }
    ]
  },
  '/branches': {
    routePath: '/branches',
    title: 'Gestión de Sucursales',
    subtitle: 'Sedes y puntos de atención del negocio',
    badge: 'Sucursales',
    description: 'Listado de todas las sucursales del negocio: alta, edición, habilitación y eliminación, sujeto al límite de sucursales activas de tu plan.',
    steps: [
      {
        title: 'Consultá el estado de cada sucursal',
        description: 'Categoría (Estética, Deportes, Salud, Gym o General), dirección, teléfono y si está habilitada o deshabilitada.',
        icon: 'store'
      },
      {
        title: 'Creá una nueva sucursal',
        description: 'Si ya alcanzaste el límite de sucursales activas de tu plan, el sistema te avisa antes de dejarte continuar.',
        icon: 'add_business'
      },
      {
        title: 'Habilitá o deshabilitá una sede',
        description: 'Deshabilitar una sucursal la oculta de las opciones de reserva sin borrar su información ni su historial.',
        icon: 'toggle_on'
      },
      {
        title: 'Eliminá una sucursal',
        description: 'Solo está disponible para sucursales deshabilitadas y sin historial (turnos, ventas o cajas). Si tiene datos históricos, el sistema bloquea el borrado y sugiere mantenerla deshabilitada.',
        icon: 'delete'
      }
    ],
    quickActions: [
      { label: 'Nueva Sucursal', route: '/branches/new' }
    ]
  },
  '/branches/new': {
    routePath: '/branches/new',
    title: 'Nueva Sucursal',
    subtitle: 'Alta de una sede del negocio',
    badge: 'Sucursales',
    description: 'Formulario para dar de alta una nueva sucursal: datos de contacto y la categoría o vertical de negocio a la que pertenece.',
    steps: [
      {
        title: 'Cargá los datos básicos',
        description: 'Nombre, dirección y teléfono de la sede.',
        icon: 'edit_note'
      },
      {
        title: 'Elegí la categoría del negocio',
        description: 'Estética, Deportes, Salud, Gym o General: esto adapta ciertas opciones específicas del sistema para ese rubro.',
        icon: 'category'
      },
      {
        title: 'Asigná la empresa dueña (solo SuperAdmin)',
        description: 'Si operás como SuperAdmin, tenés que elegir a qué empresa pertenece la sucursal antes de crearla.',
        icon: 'business'
      },
      {
        title: 'Guardá la sucursal',
        description: 'Queda habilitada por defecto y disponible de inmediato en el selector de sucursales.',
        icon: 'check_circle'
      }
    ],
    quickActions: [
      { label: 'Ver Sucursales', route: '/branches' }
    ]
  },
  '/branches/:id': {
    routePath: '/branches/:id',
    title: 'Editar Sucursal',
    subtitle: 'Datos y catálogo de servicios de la sede',
    badge: 'Sucursales',
    description: 'Edición del perfil de una sucursal existente y configuración de qué servicios del catálogo general están disponibles en esta sede, con precios y duraciones personalizados por sucursal.',
    steps: [
      {
        title: 'Actualizá el perfil de la sucursal',
        description: 'Nombre, dirección, teléfono, categoría y estado habilitado o deshabilitado.',
        icon: 'store'
      },
      {
        title: 'Activá los servicios de esta sede',
        description: 'Desde la pestaña de Servicios, habilitá o deshabilitá cada servicio del catálogo general específicamente para esta sucursal.',
        icon: 'design_services'
      },
      {
        title: 'Personalizá precio y duración por sede',
        description: 'Cada servicio puede tener un precio o duración distintos a los del catálogo general, sin afectar a las demás sucursales.',
        icon: 'price_change'
      },
      {
        title: 'Configurá variantes de servicio',
        description: 'El ícono de variantes te permite definir combinaciones específicas (por ejemplo, distintas duraciones o precios) exclusivas de esta sede.',
        icon: 'tune'
      }
    ],
    quickActions: [
      { label: 'Ver Sucursales', route: '/branches' },
      { label: 'Nueva Sucursal', route: '/branches/new' }
    ]
  },
  '/staff': {
    routePath: '/staff',
    title: 'Gestión de Staff',
    subtitle: 'Empleados y profesionales del negocio',
    badge: 'Staff',
    description: 'Listado del equipo del negocio: administradores y profesionales (recursos) que atienden turnos. Desde acá creás cuentas, asignás sucursales y configurás horarios de atención.',
    steps: [
      {
        title: 'Filtrá por sucursal o buscá por nombre',
        description: 'Localizá rápidamente a un integrante del equipo dentro del listado paginado.',
        icon: 'filter_alt'
      },
      {
        title: 'Creá un nuevo miembro del staff',
        description: 'Alta de una cuenta con su rol (Administrador, Profesional, etc.) y las sucursales donde va a operar.',
        icon: 'person_add'
      },
      {
        title: 'Habilitá o deshabilitá un usuario',
        description: 'Un usuario deshabilitado no puede iniciar sesión ni recibir nuevas reservas, pero conserva su historial.',
        icon: 'toggle_on'
      },
      {
        title: 'Configurá horarios y servicios',
        description: 'El ícono correspondiente de cada fila te lleva a la gestión completa de qué servicios atiende ese profesional y en qué franjas horarias.',
        icon: 'event_available'
      }
    ],
    quickActions: [
      { label: 'Nuevo Miembro del Staff', route: '/staff/new' }
    ]
  },
  '/staff/new': {
    routePath: '/staff/new',
    title: 'Nuevo Miembro del Staff',
    subtitle: 'Alta de una cuenta de equipo',
    badge: 'Staff',
    description: 'Formulario para crear una cuenta de acceso al sistema: datos de contacto, rol y las sucursales donde la persona va a operar.',
    steps: [
      {
        title: 'Cargá los datos de contacto',
        description: 'Nombre, email (se usa para iniciar sesión) y teléfono.',
        icon: 'edit_note'
      },
      {
        title: 'Elegí el rol',
        description: 'Define qué puede ver y hacer en el sistema: Administrador, Profesional/Recurso, u otros roles disponibles según tus permisos.',
        icon: 'admin_panel_settings'
      },
      {
        title: 'Asigná las sucursales',
        description: 'Un profesional solo puede operar y recibir turnos en las sucursales que le asignes acá.',
        icon: 'store'
      },
      {
        title: 'Guardá y continuá con horarios',
        description: 'Una vez creado, andá al listado y usá "Configurar horarios y servicios" para terminar de habilitarlo a atender turnos.',
        icon: 'check_circle'
      }
    ],
    quickActions: [
      { label: 'Ver Staff', route: '/staff' }
    ]
  },
  '/staff/:id': {
    routePath: '/staff/:id',
    title: 'Editar Staff',
    subtitle: 'Datos, rol y sucursales del profesional',
    badge: 'Staff',
    description: 'Edición del perfil de un integrante del equipo: datos de contacto, rol, sucursales asignadas y contraseña.',
    steps: [
      {
        title: 'Actualizá datos y rol',
        description: 'Nombre, contacto y el rol que determina sus permisos en el sistema.',
        icon: 'edit'
      },
      {
        title: 'Ajustá las sucursales asignadas',
        description: 'Agregar o quitar sucursales cambia dónde puede operar y recibir turnos este profesional.',
        icon: 'store'
      },
      {
        title: 'Restablecé la contraseña (opcional)',
        description: 'Dejá el campo vacío para no modificarla; completalo solo si necesitás forzar un cambio de contraseña.',
        icon: 'lock_reset'
      },
      {
        title: 'Guardá los cambios',
        description: 'Un Administrador regular no puede editar el perfil de un SuperAdmin: esa acción queda bloqueada por seguridad.',
        icon: 'check_circle'
      }
    ],
    quickActions: [
      { label: 'Ver Staff', route: '/staff' },
      { label: 'Nuevo Miembro del Staff', route: '/staff/new' }
    ]
  },
  '/staff/manage': {
    routePath: '/staff/manage',
    title: 'Gestión de Horarios y Servicios',
    subtitle: 'Qué atiende cada profesional y su disponibilidad',
    badge: 'Staff',
    description: 'Configuración detallada de qué servicios atiende un profesional en una sucursal específica, y en qué días y horarios está disponible para cada uno.',
    steps: [
      {
        title: 'Elegí la sucursal',
        description: 'Un profesional puede tener servicios y horarios distintos en cada sucursal donde trabaja.',
        icon: 'store'
      },
      {
        title: 'Seleccioná uno o varios servicios',
        description: 'Marcá los servicios que querés configurar; podés hacerlo de a uno o en bloque con la selección múltiple.',
        icon: 'design_services'
      },
      {
        title: 'Configurá la disponibilidad',
        description: 'Definí los días y franjas horarias en que el profesional atiende ese o esos servicios.',
        icon: 'event_available'
      },
      {
        title: 'Revisá el estado de cada servicio',
        description: '"Sin asignar" significa que todavía no lo atiende, "Sin horarios" que está asignado pero sin franjas configuradas, y "Configurado" que ya está listo para recibir turnos.',
        icon: 'fact_check'
      }
    ],
    quickActions: [
      { label: 'Ver Staff', route: '/staff' }
    ]
  },
  '/admin/clients': {
    routePath: '/admin/clients',
    title: 'Clientes (CRM)',
    subtitle: 'Base de clientes del negocio',
    badge: 'Clientes',
    description: 'Listado de todos los clientes registrados: alta, búsqueda, acceso a la ficha 360° y a su cuenta corriente.',
    steps: [
      {
        title: 'Buscá un cliente',
        description: 'Filtrá por nombre, email o teléfono en el buscador para encontrarlo rápidamente.',
        icon: 'search'
      },
      {
        title: 'Creá un nuevo cliente',
        description: 'Alta rápida en el CRM con sus datos de contacto y personales.',
        icon: 'person_add'
      },
      {
        title: 'Abrí la ficha 360°',
        description: 'Accedé al perfil completo, historia clínica y notas de evolución del cliente.',
        icon: 'badge'
      },
      {
        title: 'Consultá su Cuenta Corriente',
        description: 'Si el cliente tiene saldo o historial de crédito, accedé directo desde el listado.',
        icon: 'account_balance_wallet'
      }
    ],
    quickActions: [
      { label: 'Nuevo Cliente', route: '/admin/clients/new' },
      { label: 'Cuentas Corrientes', route: '/clientes/cc' }
    ]
  },
  '/admin/clients/new': {
    routePath: '/admin/clients/new',
    title: 'Nuevo Cliente',
    subtitle: 'Alta en el CRM',
    badge: 'Clientes',
    description: 'Formulario para registrar un nuevo cliente en el CRM con sus datos de contacto y personales.',
    steps: [
      {
        title: 'Cargá los datos obligatorios',
        description: 'Nombre completo y DNI son requeridos para identificar al cliente en el sistema.',
        icon: 'badge'
      },
      {
        title: 'Sumá datos de contacto',
        description: 'Email y teléfono son opcionales, pero recomendados para poder comunicarte con el cliente.',
        icon: 'contact_mail'
      },
      {
        title: 'Completá información adicional',
        description: 'Dirección, ocupación y fecha de nacimiento ayudan a personalizar la atención.',
        icon: 'edit_note'
      },
      {
        title: 'Guardá el cliente',
        description: 'Una vez creado, queda disponible para agendar turnos y registrar ventas a su nombre.',
        icon: 'check_circle'
      }
    ],
    quickActions: [
      { label: 'Ver Clientes', route: '/admin/clients' }
    ]
  },
  '/admin/clients/:id': {
    routePath: '/admin/clients/:id',
    title: 'Ficha 360° del Cliente',
    subtitle: 'Perfil, historia clínica y evolución',
    badge: 'Clientes',
    description: 'Vista integral de un cliente: sus datos de contacto, fichas clínicas dinámicas completadas y notas de evolución cargadas por los profesionales.',
    steps: [
      {
        title: 'Pestaña Perfil',
        description: 'Editá los datos de contacto y personales del cliente y guardá los cambios.',
        icon: 'badge'
      },
      {
        title: 'Pestaña Historia Clínica',
        description: 'Elegí una plantilla de formulario dinámico para completar una nueva ficha, o consultá las respuestas anteriores ya guardadas.',
        icon: 'assignment'
      },
      {
        title: 'Pestaña Evolución',
        description: 'Registrá notas de seguimiento del cliente a lo largo del tiempo; podés editarlas o eliminarlas después.',
        icon: 'history_edu'
      },
      {
        title: 'Volvé al listado',
        description: 'El botón de volver te lleva de nuevo al listado general de clientes.',
        icon: 'arrow_back'
      }
    ],
    quickActions: [
      { label: 'Ver Clientes', route: '/admin/clients' },
      { label: 'Cuentas Corrientes', route: '/clientes/cc' }
    ]
  },
  '/clientes/cc': {
    routePath: '/clientes/cc',
    title: 'Cuentas Corrientes',
    subtitle: 'Saldos de clientes con crédito o deuda',
    badge: 'Clientes',
    description: 'Listado consolidado de saldos de cuenta corriente de todos los clientes: cuánto deben, cuánto tienen a favor y el neto resultante.',
    steps: [
      {
        title: 'Buscá un cliente',
        description: 'Filtrá por nombre para encontrar rápidamente su cuenta.',
        icon: 'search'
      },
      {
        title: 'Revisá el saldo neto',
        description: 'Un saldo positivo indica crédito a favor del cliente; uno negativo indica deuda pendiente de cobro.',
        icon: 'account_balance_wallet'
      },
      {
        title: 'Consultá los movimientos',
        description: 'La columna de movimientos muestra cuántas operaciones tiene registradas esa cuenta corriente.',
        icon: 'receipt_long'
      },
      {
        title: 'Abrí el detalle',
        description: 'Accedé a la cuenta corriente completa de un cliente para ver su historial y registrar pagos.',
        icon: 'open_in_new'
      }
    ],
    quickActions: [
      { label: 'Ver Clientes (CRM)', route: '/admin/clients' }
    ]
  },
  '/clientes/cc/:id': {
    routePath: '/clientes/cc/:id',
    title: 'Estado de Cuenta Corriente',
    subtitle: 'Deudas, créditos y registro de pagos',
    badge: 'Clientes',
    description: 'Detalle completo de la cuenta corriente de un cliente: balance de deudas y créditos, y registro de nuevos pagos.',
    steps: [
      {
        title: 'Revisá el balance',
        description: 'Deudas (ventas o consumos pendientes), créditos (saldo a favor) y el neto consolidado entre ambos.',
        icon: 'account_balance_wallet'
      },
      {
        title: 'Pestaña Saldos Pendientes',
        description: 'Listado operativo de las deudas activas, con una acción rápida para cobrar cada una individualmente.',
        icon: 'pending_actions'
      },
      {
        title: 'Pestaña Historial Completo',
        description: 'Todos los movimientos históricos de la cuenta, con su origen y el saldo acumulado en cada punto.',
        icon: 'history'
      },
      {
        title: 'Registrá un pago',
        description: 'Necesitás una caja diaria abierta en la sucursal activa. Si cobrás una deuda puntual, el sistema aplica automáticamente cualquier saldo a favor existente antes de pedirte el resto en efectivo fresco.',
        icon: 'payments'
      }
    ],
    quickActions: [
      { label: 'Cuentas Corrientes', route: '/clientes/cc' },
      { label: 'Ver Clientes (CRM)', route: '/admin/clients' }
    ]
  },
  '/forms': {
    routePath: '/forms',
    title: 'Formularios Dinámicos',
    subtitle: 'Plantillas de fichas personalizadas',
    badge: 'Formularios',
    description: 'Listado de plantillas de formularios dinámicos que se completan por cliente (por ejemplo, fichas de anamnesis o consentimientos). Desde acá creás, editás y activás o desactivás plantillas.',
    steps: [
      {
        title: 'Buscá una plantilla',
        description: 'Filtrá por nombre en el listado para encontrarla rápidamente.',
        icon: 'search'
      },
      {
        title: 'Creá una nueva plantilla',
        description: 'Diseñá los campos que va a tener el formulario dinámico.',
        icon: 'add_circle'
      },
      {
        title: 'Habilitá o deshabilitá una plantilla',
        description: 'Una plantilla deshabilitada no se puede usar para nuevas fichas, pero conserva las respuestas ya guardadas.',
        icon: 'toggle_on'
      },
      {
        title: 'Editá una plantilla existente',
        description: 'Ajustá título y campos. Si ya tiene respuestas cargadas, algunos cambios estructurales quedan bloqueados para no perder datos.',
        icon: 'edit'
      }
    ],
    quickActions: [
      { label: 'Nueva Plantilla', route: '/forms/new' }
    ]
  },
  '/forms/new': {
    routePath: '/forms/new',
    title: 'Nueva Plantilla de Formulario',
    subtitle: 'Diseñá los campos de una ficha dinámica',
    badge: 'Formularios',
    description: 'Constructor de formularios personalizados: definí el título y agregá los campos que vas a necesitar completar por cada cliente.',
    steps: [
      {
        title: 'Ponele un título',
        description: 'Identifica la plantilla en los listados y en la ficha del cliente.',
        icon: 'title'
      },
      {
        title: 'Agregá campos',
        description: 'Elegí el tipo de cada uno: Texto Corto, Texto Largo, Número, Fecha, Selección o Casilla.',
        icon: 'add_box'
      },
      {
        title: 'Configurá las opciones de Selección',
        description: 'Si el campo es de tipo Selección, cargá las opciones disponibles una por una.',
        icon: 'list'
      },
      {
        title: 'Reordená y guardá',
        description: 'Arrastrá los campos para cambiar su orden, marcá cuáles son obligatorios, y guardá la plantilla.',
        icon: 'drag_indicator'
      }
    ],
    quickActions: [
      { label: 'Ver Formularios', route: '/forms' }
    ]
  },
  '/forms/edit': {
    routePath: '/forms/edit',
    title: 'Editar Plantilla de Formulario',
    subtitle: 'Modificar campos de una ficha existente',
    badge: 'Formularios',
    description: 'Edición de una plantilla ya creada. Si la plantilla ya tiene respuestas guardadas de clientes, el sistema bloquea eliminar o reordenar campos existentes para no perder la integridad de esos datos.',
    steps: [
      {
        title: 'Revisá los campos actuales',
        description: 'El formulario carga con el título y todos los campos ya definidos.',
        icon: 'description'
      },
      {
        title: 'Sumá campos nuevos',
        description: 'Siempre podés agregar campos adicionales, tenga o no respuestas la plantilla.',
        icon: 'add_box'
      },
      {
        title: 'Cuidado con plantillas en uso',
        description: 'Si ya hay respuestas guardadas, no vas a poder eliminar ni reordenar los campos existentes, solo agregar nuevos o editar textos.',
        icon: 'lock'
      },
      {
        title: 'Guardá los cambios',
        description: 'Los ajustes se aplican a la plantilla; las respuestas ya cargadas por los clientes no se modifican.',
        icon: 'check_circle'
      }
    ],
    quickActions: [
      { label: 'Ver Formularios', route: '/forms' }
    ]
  },
  '/services': {
    routePath: '/services',
    title: 'Catálogo de Servicios',
    subtitle: 'Servicios ofrecidos por el negocio',
    badge: 'Servicios',
    description: 'Catálogo maestro de servicios de la empresa: precio base, duración y variantes. Para habilitarlos en una sucursal puntual o asignarlos a un profesional, hacelo desde Sucursales o Staff.',
    steps: [
      {
        title: 'Buscá o filtrá servicios',
        description: 'Filtrá por sucursal, empresa o nombre para encontrar rápidamente un servicio.',
        icon: 'filter_alt'
      },
      {
        title: 'Creá un nuevo servicio',
        description: 'Definí título, precio y duración base, o marcalo como un servicio con variantes.',
        icon: 'add_circle'
      },
      {
        title: 'Activá o desactivá un servicio',
        description: 'Un servicio inactivo no se puede ofrecer ni agendar en ninguna sucursal.',
        icon: 'toggle_on'
      },
      {
        title: 'Eliminá un servicio',
        description: 'Si tiene turnos históricos asociados, el sistema bloquea el borrado para proteger la integridad de los datos; si solo tiene configuraciones de sede o variantes, te avisa que se eliminarán junto con él.',
        icon: 'delete'
      }
    ],
    quickActions: [
      { label: 'Nuevo Servicio', route: '/services/new' },
      { label: 'Ver Sucursales', route: '/branches' }
    ]
  },
  '/services/new': {
    routePath: '/services/new',
    title: 'Nuevo Servicio',
    subtitle: 'Alta en el catálogo, con variantes opcionales',
    badge: 'Servicios',
    description: 'Formulario en dos pasos para crear un servicio: datos base y, si corresponde, sus variantes (por ejemplo, distintas duraciones o niveles de precio para el mismo servicio).',
    steps: [
      {
        title: 'Cargá los datos base',
        description: 'Título, descripción, precio y duración del servicio.',
        icon: 'edit_note'
      },
      {
        title: 'Activá "Tiene Variantes" si aplica',
        description: 'Al activarlo, el precio y la duración base se ponen en 0 porque cada variante va a definir los suyos propios.',
        icon: 'tune'
      },
      {
        title: 'Guardá el servicio',
        description: 'Si tiene variantes, avanzás automáticamente al segundo paso para cargarlas; si no, volvés directo al catálogo.',
        icon: 'check_circle'
      },
      {
        title: 'Cargá las variantes',
        description: 'Cada variante tiene su propio título, precio adicional y duración adicional sobre el servicio base.',
        icon: 'list_alt'
      }
    ],
    quickActions: [
      { label: 'Ver Servicios', route: '/services' }
    ]
  },
  '/services/edit': {
    routePath: '/services/edit',
    title: 'Editar Servicio',
    subtitle: 'Datos base y variantes',
    badge: 'Servicios',
    description: 'Edición de un servicio existente del catálogo: datos base y gestión completa de sus variantes.',
    steps: [
      {
        title: 'Actualizá los datos base',
        description: 'Título, descripción, precio y duración, si el servicio no maneja variantes.',
        icon: 'edit'
      },
      {
        title: 'Gestioná las variantes',
        description: 'Agregá, editá o eliminá variantes con su propio precio y duración adicional.',
        icon: 'list_alt'
      },
      {
        title: 'Guardá los cambios',
        description: 'Los ajustes impactan en todas las sucursales que tengan este servicio habilitado.',
        icon: 'check_circle'
      },
      {
        title: 'Personalizá por sucursal',
        description: 'Si necesitás un precio o duración distintos en una sede puntual, hacelo desde la edición de esa sucursal, no desde acá.',
        icon: 'store'
      }
    ],
    quickActions: [
      { label: 'Ver Servicios', route: '/services' }
    ]
  },
  '/admin/my-organization': {
    routePath: '/admin/my-organization',
    title: 'Mi Organización',
    subtitle: 'Datos de la empresa y facturación AFIP',
    badge: 'Configuración',
    description: 'Configuración general de tu empresa: identidad (nombre, slug, dirección) y el vínculo con AFIP para poder emitir comprobantes electrónicos.',
    steps: [
      {
        title: 'Editá los datos de la organización',
        description: 'Nombre, identificador único (slug) y dirección. El slug se genera automáticamente a partir del nombre si no lo modificás manualmente.',
        icon: 'business'
      },
      {
        title: 'Elegí el modo de lista de precios',
        description: 'Define cómo se manejan los precios en el negocio.',
        icon: 'sell'
      },
      {
        title: 'Activá la facturación electrónica',
        description: 'Completá el formulario con tu CUIT, razón social, domicilio fiscal y condición frente al IVA para iniciar el alta ante AFIP.',
        icon: 'receipt_long'
      },
      {
        title: 'Seguí el estado de la delegación AFIP',
        description: 'Si tu vínculo quedó "pendiente de delegación", vas a ver instrucciones con el CUIT maestro de KronoFlow para completar la autorización desde el portal de AFIP.',
        icon: 'verified_user'
      }
    ],
    quickActions: [
      { label: 'Formas de Pago', route: '/admin/payment-methods' },
      { label: 'Facturación Electrónica', route: '/billing' }
    ]
  },
  '/admin/payment-methods': {
    routePath: '/admin/payment-methods',
    title: 'Formas de Pago',
    subtitle: 'Medios de cobro habilitados por sucursal',
    badge: 'Configuración',
    description: 'Catálogo de formas de pago del negocio (efectivo, tarjeta, transferencia, billetera digital) con recargos/descuentos y cuotas configurables. Podés personalizar cada una por sucursal.',
    steps: [
      {
        title: 'Elegí la vista: global o por sucursal',
        description: 'Sin una sucursal seleccionada arriba, ves el catálogo general de la empresa. Con una sucursal elegida, ves su configuración puntual, incluyendo sobrescrituras propias.',
        icon: 'store'
      },
      {
        title: 'Creá una nueva forma de pago',
        description: 'Definí nombre, tipo (efectivo, tarjeta, transferencia o billetera), recargo o descuento asociado, y cantidad de cuotas si aplica.',
        icon: 'add_card'
      },
      {
        title: 'Habilitá o deshabilitá por sucursal',
        description: 'Cada sucursal puede tener sus propias formas de pago activas; una deshabilitada no aparece como opción al cobrar en esa sede.',
        icon: 'toggle_on'
      },
      {
        title: 'Personalizá por sucursal',
        description: 'Desde la edición podés sobrescribir nombre, tipo, recargo/descuento o cuotas de una forma de pago solo para una sucursal puntual, sin afectar al resto.',
        icon: 'tune'
      }
    ],
    quickActions: [
      { label: 'Nueva Forma de Pago', route: '/admin/payment-methods/new' },
      { label: 'Ver Sucursales', route: '/branches' }
    ]
  },
  '/admin/payment-methods/new': {
    routePath: '/admin/payment-methods/new',
    title: 'Nueva Forma de Pago',
    subtitle: 'Alta de un medio de cobro',
    badge: 'Configuración',
    description: 'Formulario para dar de alta una nueva forma de pago disponible en el negocio.',
    steps: [
      {
        title: 'Elegí el tipo',
        description: 'Efectivo, Tarjeta, Transferencia bancaria, QR o Mercado Pago.',
        icon: 'payments'
      },
      {
        title: 'Configurá el ajuste',
        description: 'Opcionalmente aplicá un recargo o descuento porcentual sobre el monto cuando se use esta forma de pago.',
        icon: 'percent'
      },
      {
        title: 'Definí las cuotas',
        description: 'Si es un plan en cuotas, indicá cuántas; por defecto es 1 (pago único).',
        icon: 'calendar_view_week'
      },
      {
        title: 'Guardá la forma de pago',
        description: 'Queda disponible para habilitar en cada sucursal desde el listado.',
        icon: 'check_circle'
      }
    ],
    quickActions: [
      { label: 'Ver Formas de Pago', route: '/admin/payment-methods' }
    ]
  },
  '/admin/payment-methods/edit': {
    routePath: '/admin/payment-methods/edit',
    title: 'Editar Forma de Pago',
    subtitle: 'Datos generales y sobrescrituras por sucursal',
    badge: 'Configuración',
    description: 'Edición de una forma de pago existente. Si no tenés una sucursal seleccionada (vista global), además podés personalizarla de forma independiente para cada sucursal.',
    steps: [
      {
        title: 'Actualizá los datos generales',
        description: 'Nombre, tipo, recargo/descuento y cuotas por defecto.',
        icon: 'edit'
      },
      {
        title: 'Revisá las sobrescrituras por sucursal',
        description: 'En la vista global, cada sucursal aparece con sus valores efectivos: propios si los personalizaste, o heredados del general si no.',
        icon: 'store'
      },
      {
        title: 'Personalizá una sucursal puntual',
        description: 'Sobrescribí nombre, tipo, ajuste o cuotas solo para esa sede, sin tocar la configuración general ni las demás sucursales.',
        icon: 'tune'
      },
      {
        title: 'Guardá los cambios',
        description: 'Los cambios generales impactan en todas las sucursales que no tengan una sobrescritura propia.',
        icon: 'check_circle'
      }
    ],
    quickActions: [
      { label: 'Ver Formas de Pago', route: '/admin/payment-methods' }
    ]
  },
  '/admin/config/categories': {
    routePath: '/admin/config/categories',
    title: 'Categorías de Caja',
    subtitle: 'Clasificación de ingresos y egresos',
    badge: 'Configuración',
    description: 'Categorías usadas para clasificar los movimientos manuales de Caja Diaria (ingresos y egresos). Incluye categorías del sistema, no editables, y personalizadas propias del negocio.',
    steps: [
      {
        title: 'Distinguí categorías del sistema y personalizadas',
        description: 'Las del sistema vienen predefinidas y no se pueden editar ni eliminar; las personalizadas son tuyas y las administrás libremente.',
        icon: 'category'
      },
      {
        title: 'Creá una categoría personalizada',
        description: 'Definí un nombre y si es de Ingreso o Egreso.',
        icon: 'add_circle'
      },
      {
        title: 'Editá o eliminá una categoría propia',
        description: 'Solo se puede eliminar si no tiene movimientos de caja ya registrados con ella; si está en uso, el sistema avisa y bloquea el borrado.',
        icon: 'delete'
      },
      {
        title: 'Usalas al registrar movimientos',
        description: 'Estas categorías son las que vas a elegir después al cargar un ingreso o egreso manual en Caja Diaria.',
        icon: 'point_of_sale'
      }
    ],
    quickActions: [
      { label: 'Caja Diaria', route: '/finance/cash/daily' }
    ]
  },
  '/admin/config/administrative-categories': {
    routePath: '/admin/config/administrative-categories',
    title: 'Categorías de Gastos Administrativos',
    subtitle: 'Clasificación de gastos fijos y variables',
    badge: 'Configuración',
    description: 'Categorías propias del negocio para clasificar los Gastos Administrativos (alquiler, servicios, sueldos, etc.), tanto los manuales como los generados por plantillas recurrentes.',
    steps: [
      {
        title: 'Creá una categoría',
        description: 'Definí un nombre para agrupar tus gastos administrativos.',
        icon: 'add_circle'
      },
      {
        title: 'Editá una categoría existente',
        description: 'Renombrala en línea, directamente desde el listado.',
        icon: 'edit'
      },
      {
        title: 'Eliminá una categoría sin uso',
        description: 'Solo se puede borrar si no tiene plantillas o gastos ya vinculados; si está en uso, el sistema avisa cuántos registros la usan y bloquea el borrado.',
        icon: 'delete'
      },
      {
        title: 'Usalas en Gastos Administrativos',
        description: 'Estas categorías son las que vas a elegir al crear una plantilla de costo fijo o un gasto manual.',
        icon: 'receipt_long'
      }
    ],
    quickActions: [
      { label: 'Gastos Administrativos', route: '/finance/expenses' }
    ]
  },
  '/admin/sales/vouchers': {
    routePath: '/admin/sales/vouchers',
    title: 'Vales de Saldo a Favor',
    subtitle: 'Consulta, reimpresión y seguimiento',
    badge: 'Ventas',
    description: 'Vales que se entregan al devolver una venta en vale. Cada vale es de un solo uso, al portador, y se usa como parte de pago en una próxima compra. No es dinero: no sale efectivo de la caja al emitirlo.',
    steps: [
      {
        title: 'Buscá un vale',
        description: 'El buscador acepta el código o el cliente y consulta solo, a partir de 3 caracteres. Las fechas filtran por emisión.',
        icon: 'search'
      },
      {
        title: 'Filtrá por sucursal',
        description: '"Emitido en" y "Usado en" muestran los vales según dónde se emitieron o se usaron. Según su ámbito, un vale puede usarse en todas las sucursales o solo en la que lo emitió.',
        icon: 'storefront'
      },
      {
        title: 'Elegí el estado',
        description: 'Activos, Usados, Vencidos o Todos. Los filtros aplicados aparecen como chips que se quitan de a uno, y el botón Limpiar vuelve todo al valor inicial.',
        icon: 'filter_alt'
      },
      {
        title: 'Reimprimí o mirá el historial',
        description: 'La impresora reimprime el ticket. El reloj muestra la emisión, las impresiones, el uso y el vuelto. El ojo abre la venta de origen.',
        icon: 'print'
      },
      {
        title: 'Cómo se usa un vale',
        description: 'En Nueva Venta, cargá el código en el bloque "Vale de saldo a favor". El sistema valida que esté activo, no vencido y habilitado en esa sucursal.',
        icon: 'shopping_cart'
      },
      {
        title: 'Si el vale cubre más que la compra',
        description: 'Se consume entero y se emite un vale nuevo por la diferencia (vuelto). Figura con origen "Saldo de V-..." y hereda el vencimiento del vale anterior.',
        icon: 'call_split'
      },
      {
        title: 'Vencimiento',
        description: 'Lo define el dueño en Mi Organización: sin vencimiento o una cantidad de días desde la emisión. Un vale vencido no se puede usar.',
        icon: 'event'
      }
    ],
    quickActions: [
      { label: 'Nueva Venta', route: '/admin/sales/new' },
      { label: 'Ventas Realizadas', route: '/admin/sales' }
    ]
  },
  '/admin/sales/:id': {
    routePath: '/admin/sales/:id',
    title: 'Detalle de Venta',
    subtitle: 'Operación completa y auditoría',
    badge: 'Ventas',
    description: 'Vista de una venta puntual con todo lo que pasó en ella: pago, productos, liquidación, vales y devoluciones.',
    steps: [
      {
        title: 'Revisá el estado',
        description: 'Arriba se ve si la venta está cobrada o anulada, la fecha y el medio de pago utilizado con su importe.',
        icon: 'info'
      },
      {
        title: 'Mirá los productos',
        description: 'El detalle lista cada ítem con su cantidad, el precio de lista y el importe final cobrado.',
        icon: 'shopping_bag'
      },
      {
        title: 'Leé la liquidación',
        description: 'El resumen muestra el subtotal, los descuentos aplicados, el total abonado y, si hubo devolución, el total reintegrado.',
        icon: 'calculate'
      },
      {
        title: 'Imprimí los vales',
        description: 'Si la venta se anuló o se devolvió en vale, la sección "Vales de saldo a favor" muestra el código, el estado y el botón Imprimir para entregar el vale al cliente.',
        icon: 'confirmation_number'
      },
      {
        title: 'Imprimí la operación',
        description: 'El botón Imprimir genera el comprobante interno de la venta. El registro de auditoría deja constancia de la operación.',
        icon: 'print'
      }
    ],
    quickActions: [
      { label: 'Ventas Realizadas', route: '/admin/sales' },
      { label: 'Vales', route: '/admin/sales/vouchers' }
    ]
  },
  '/admin/inventory/categories': {
    routePath: '/admin/inventory/categories',
    title: 'Categorías de Productos',
    subtitle: 'Agrupación del catálogo',
    badge: 'Inventario',
    description: 'Categorías para ordenar los productos. Se usan para filtrar el inventario y para organizar la importación de productos.',
    steps: [
      {
        title: 'Buscá una categoría',
        description: 'El buscador filtra la lista por nombre.',
        icon: 'search'
      },
      {
        title: 'Leé la lista',
        description: 'Cada categoría muestra su nombre, su identificador de color y la fecha de creación.',
        icon: 'list'
      },
      {
        title: 'Creá o editá',
        description: '"Nueva Categoría" da de alta una. El lápiz edita nombre y color.',
        icon: 'edit'
      },
      {
        title: 'Eliminá con cuidado',
        description: 'El tacho elimina la categoría. Antes de hacerlo, revisá que no tenga productos que dependan de ella.',
        icon: 'delete'
      }
    ],
    quickActions: [
      { label: 'Inventario de Productos', route: '/admin/inventory' },
      { label: 'Distribución de Inventario', route: '/admin/inventory/distribution' }
    ]
  },
  default: {
    routePath: 'default',
    title: 'Centro de Ayuda',
    subtitle: 'Guía general de KronoFlow',
    badge: 'Ayuda',
    description: 'Todavía no hay una guía específica para esta sección. Explorá el menú lateral para descubrir los módulos disponibles.',
    steps: [
      {
        title: 'Usá el menú lateral',
        description: 'Todos los módulos del sistema están accesibles desde la barra de navegación izquierda.',
        icon: 'menu'
      },
      {
        title: '¿Necesitás más ayuda?',
        description: 'Contactá al equipo de soporte de KronoFlow si tenés dudas sobre esta sección.',
        icon: 'support_agent'
      }
    ]
  }
};
